/// Phone/tablet hint from the request, used so the first HTML matches the
/// viewport instead of always painting the desktop tree and swapping later.
/// `Sec-CH-UA-Mobile` wins when the browser sends it (Lighthouse does).
/// Otherwise fall back to the User-Agent. A missing hint stays desktop so
/// cached shells do not flash a phone layout on a laptop.
export function detectMobileRequest(headerStore: {
	get(name: string): string | null
}): boolean {
	const clientHint = headerStore.get('sec-ch-ua-mobile')?.trim().toLowerCase()
	if (clientHint === '?1') return true
	if (clientHint === '?0') return false

	const ua = headerStore.get('user-agent') || ''
	return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua)
}

export const SEEDED_MOBILE_WIDTH = 390
export const SEEDED_MOBILE_HEIGHT = 844
export const SEEDED_TABLET_WIDTH = 834
export const SEEDED_TABLET_HEIGHT = 1112
export const SEEDED_DESKTOP_WIDTH = 1440
export const SEEDED_DESKTOP_HEIGHT = 900

const PHONE_BREAK = 736
const DESKTOP_BREAK = 1024

export type ViewportSeed = { mobile: boolean; width: number; height: number }

/// Layout seed for the collection page. A real viewport width wins, so a
/// narrowed desktop window does not paint the 1440px tree. Tablets (iPad,
/// Android without "Mobile") get a width under 1024 instead of a phone or a
/// laptop. Phones stay at 390.
export function viewportSeedFromHeaders(headerStore: {
	get(name: string): string | null
}): ViewportSeed {
	const raw = headerStore.get('sec-ch-viewport-width')?.trim()
	const parsed = raw ? Number.parseInt(raw, 10) : NaN
	if (Number.isFinite(parsed) && parsed > 0) {
		const width = Math.min(3840, Math.max(320, parsed))
		return {
			width,
			height: width < DESKTOP_BREAK ? SEEDED_MOBILE_HEIGHT : SEEDED_DESKTOP_HEIGHT,
			mobile: width < PHONE_BREAK
		}
	}

	const hint = headerStore.get('sec-ch-ua-mobile')?.trim().toLowerCase()
	const ua = headerStore.get('user-agent') || ''
	const platform = (headerStore.get('sec-ch-ua-platform') || '').replace(/"/g, '').toLowerCase()
	const androidTablet = platform === 'android' && !/Mobile/i.test(ua)
	const iosTablet = /iPad/.test(ua) || (platform === 'ios' && hint !== '?1' && !/iPhone|iPod/i.test(ua))
	if (hint !== '?1' && (androidTablet || iosTablet)) {
		return { mobile: false, width: SEEDED_TABLET_WIDTH, height: SEEDED_TABLET_HEIGHT }
	}

	if (detectMobileRequest(headerStore)) {
		return { mobile: true, width: SEEDED_MOBILE_WIDTH, height: SEEDED_MOBILE_HEIGHT }
	}
	return { mobile: false, width: SEEDED_DESKTOP_WIDTH, height: SEEDED_DESKTOP_HEIGHT }
}
